// This file configures the initialization of Sentry on the client.
// The added config here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import { ignoreErrors } from '@repo/typescript-config/ignore-sentry-errors'
import * as Sentry from '@sentry/nextjs'

// Skip Sentry initialization for localhost/development
if (process.env.NODE_ENV !== 'development') {
  Sentry.init({
    dsn: 'https://724aeb9694f4226d82951bf2d8164c52@sentry.hamravesh.com/8485',

    // Add optional integrations for additional features
    integrations: [Sentry.replayIntegration()],

    // Define how likely traces are sampled. Adjust this value in production, or use tracesSampler for greater control.
    tracesSampleRate: 1,

    // Define how likely Replay events are sampled.
    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: 0.1,

    // Define how likely Replay events are sampled when an error occurs.
    replaysOnErrorSampleRate: 1.0,

    // Enable logs to be sent to Sentry
    enableLogs: true,

    // Setting this option to true will print useful information to the console while you're setting up Sentry.
    debug: false,
    enabled: Boolean(process.env.NEXT_PUBLIC_ENABLE_SENTRY),
    ignoreErrors: ignoreErrors,

    // Transport configuration to send requests in background without blocking
    // Use browser offline transport with fetch transport for async sending
    // This ensures requests are sent asynchronously without blocking the main thread
    transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),

    // Delay sending events to prevent blocking initial page load
    // Events will be queued and sent when browser is idle or after timeout
    beforeSend(event) {
      // Return event immediately - Sentry will handle queuing and async sending
      // The transport will use fetch in background without blocking
      return event
    },

    // Use requestIdleCallback for non-critical transaction events
    beforeSendTransaction(event) {
      // For transaction events, delay sending until browser is idle
      // This prevents blocking critical page load operations
      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        // Queue the event and send it when browser is idle
        // Sentry will handle the actual sending asynchronously
        const idleCallback =
          window.requestIdleCallback || ((cb: () => void) => setTimeout(cb, 0))
        idleCallback(
          () => {
            // Event will be sent automatically by Sentry queue
          },
          { timeout: 1000 },
        )
      }
      return event
    },
  })
}

export const onRouterTransitionStart =
  process.env.NODE_ENV !== 'development'
    ? Sentry.captureRouterTransitionStart
    : () => {}
